import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { INDEX_WORK_YIELD_EVERY } from './constants.js';
import { withCodeNavCacheSqlite, withCodeNavCacheSqliteSync } from '../../sqlite/code-nav-cache/database.js';
import type { Database } from 'node-sqlite3-wasm';

function escapeLikePattern(fragment: string): string {
  return fragment.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function bridgeFileIndexIsPopulatedWithDb(resolvedCwd: string, db: Database): boolean {
  const row = db.get('SELECT 1 as ok FROM file_index_parent_path WHERE path = ? LIMIT 1', [resolvedCwd]) as
    | { ok: number }
    | null;
  return row != null;
}

function bridgeFileIndexPathCountWithDb(resolvedCwd: string, db: Database): number {
  const row = db.get(
    `SELECT COUNT(*) as c
     FROM file_index_child_path child
     JOIN file_index_parent_path parent ON parent.id = child.parent_id
     WHERE parent.path = ?`,
    [resolvedCwd],
  ) as { c: number | bigint } | null;
  const c = row?.c ?? 0;
  return Number(c);
}

function searchBridgeFilePathsWithDb(resolvedCwd: string, query: string, limit: number, db: Database): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const pattern = `%${escapeLikePattern(q)}%`;
  const lim = Math.max(0, Math.min(10_000, Math.floor(limit)));
  const rows = db.all(
    `SELECT child.path
     FROM file_index_child_path child
     JOIN file_index_parent_path parent ON parent.id = child.parent_id
     WHERE parent.path = ? AND lower(child.path) LIKE ? ESCAPE '\\'
     ORDER BY child.path
     LIMIT ?`,
    [resolvedCwd, pattern, lim],
  ) as Array<{ path: string }>;
  return rows.map((r) => String(r.path));
}

/** True when the SQLite-backed path cache has at least one row for this cwd. */
export async function bridgeFileIndexIsPopulated(resolvedCwd: string): Promise<boolean> {
  return withCodeNavCacheSqlite((db) => bridgeFileIndexIsPopulatedWithDb(resolvedCwd, db));
}

export async function bridgeFileIndexPathCount(resolvedCwd: string): Promise<number> {
  return withCodeNavCacheSqlite((db) => bridgeFileIndexPathCountWithDb(resolvedCwd, db));
}

/**
 * Substring search over cached paths for one cwd. Uses SQLite `LIKE` with `ESCAPE` (substring / trigram optimization in SQLite 3.43+).
 * Dependency install directories are skipped during indexing.
 */
export function searchBridgeFilePaths(resolvedCwd: string, query: string, limit: number = 100): string[] {
  return withCodeNavCacheSqliteSync((db) => searchBridgeFilePathsWithDb(resolvedCwd, query, limit, db));
}

/** Like {@link searchBridgeFilePaths}, yielding periodically for large result scans. */
export async function searchBridgeFilePathsAsync(
  resolvedCwd: string,
  query: string,
  limit: number = 100,
): Promise<string[]> {
  await yieldToEventLoop();
  const out = await withCodeNavCacheSqlite((db) => searchBridgeFilePathsWithDb(resolvedCwd, query, limit, db));
  if (out.length >= INDEX_WORK_YIELD_EVERY) await yieldToEventLoop();
  return out;
}
